import {
  config,
  type CognitoServiceConfig as EnvCognitoConfig,
  type CognitoServiceKey,
} from "../../shared/config/environment";
import type { ConnectionAuthConfig } from "../auth/types";

/**
 * Per-connector Cognito credentials, env-driven.
 *
 * The Studio settings endpoint returns connections as
 * `{name, systemName, status}` only — Cognito credentials (clientId, poolId,
 * hostedUiDomain, region, identityProvider, idpIdentifier, tokenEndpoint)
 * never travel over the wire. Secret-bearing fields (clientId, poolId,
 * apiKey) come from `VITE_COGNITO_<SERVICE>_<FIELD>` envs (or runtime
 * `globalThis.__CONFIG__.COGNITO` overrides) via `config.cognito[service]`.
 * Non-secret fields (hostedUiDomain, region, identityProvider) have baked-in
 * defaults below, copied from the legacy prototype's config.js so the add-in
 * boots against the right pools without extra setup.
 */

interface AuthConfigDefaults {
  hostedUiDomain: string;
  region: string;
  identityProvider?: string;
  idpIdentifier?: string;
  tokenEndpoint?: string;
}

const DEFAULTS: Record<CognitoServiceKey, AuthConfigDefaults> = {
  engage: {
    hostedUiDomain: "bat-nonorg-wl-uatengage.auth.us-east-1.amazoncognito.com",
    region: "us-east-1",
    identityProvider: "Azure",
  },
  myqueue: {
    hostedUiDomain: "bcg-wl-uatengage.auth.us-east-1.amazoncognito.com",
    region: "us-east-1",
    // The legacy prototype's config.js says "AzureSAML", but the IdP on
    // the deployed Cognito client is named "Azure" — sending "AzureSAML"
    // triggers Cognito's IdP picker with "Login option is not available".
    identityProvider: "Azure",
  },
  logocloud: {
    // LogoCloud has a dual-region quirk: the Cognito user pool lives in
    // us-east-1 (hostedUiDomain + poolId), but the LogoCloud API runs in
    // eu-west-2 and expects "eu-west-2" in the `region` header.
    hostedUiDomain: "wl-stg-logostore.auth.us-east-1.amazoncognito.com",
    region: "eu-west-2",
    identityProvider: "wl-stg-logostore-idp",
  },
  transcribe: {
    hostedUiDomain: "wl-idp-stg-web.auth.us-east-1.amazoncognito.com",
    region: "us-east-1",
    identityProvider: "wl-idp-oidc",
    idpIdentifier: "williamslea.com",
  },
};

function isCognitoServiceKey(key: string): key is CognitoServiceKey {
  return key in DEFAULTS;
}

function buildAuthConfig(
  env: EnvCognitoConfig,
  defaults: AuthConfigDefaults,
): ConnectionAuthConfig {
  const merged: ConnectionAuthConfig = {
    clientId: env.clientId,
    hostedUiDomain: env.hostedUiDomain || defaults.hostedUiDomain,
    poolId: env.poolId,
    region: env.region || defaults.region,
  };
  const apiKey = env.apiKey;
  if (apiKey) merged.apiKey = apiKey;
  const identityProvider = env.identityProvider || defaults.identityProvider;
  if (identityProvider) merged.identityProvider = identityProvider;
  const idpIdentifier = env.idpIdentifier || defaults.idpIdentifier;
  if (idpIdentifier) merged.idpIdentifier = idpIdentifier;
  const tokenEndpoint = env.tokenEndpoint || defaults.tokenEndpoint;
  if (tokenEndpoint) merged.tokenEndpoint = tokenEndpoint;
  return merged;
}

/**
 * Returns the auth config for a connection's `systemName` / `addinKey`, or
 * undefined when the system isn't a known Cognito connector (so non-Cognito
 * connections from the backend pass through untouched).
 */
export function getConnectionAuthConfig(
  addinKey: string,
): ConnectionAuthConfig | undefined {
  if (!isCognitoServiceKey(addinKey)) return undefined;
  return buildAuthConfig(config.cognito[addinKey], DEFAULTS[addinKey]);
}
